import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { getDashboardStats, getStaff, getDistributorBalance, getDistributors, getOwnerDashboardSummary, getPaymentRecovery, api } from '../lib/api';
import { SearchableSelect } from '../components/ui/searchable-select';
import TestRunnerLauncher from '../components/TestRunnerLauncher';
import SystemHealthWidget from '../components/SystemHealthWidget';
import DailySummaryWidget from '../components/DailySummaryWidget';
import CriticalRecoveryBanner from '../components/dashboard/CriticalRecoveryBanner';
import RecoveryEmailHistoryWidget from '../components/dashboard/RecoveryEmailHistoryWidget';
import LowBalanceWarning from '../components/dashboard/LowBalanceWarning';
import PendingRecoveryCard from '../components/dashboard/PendingRecoveryCard';
import TodaysActionItems from '../components/dashboard/TodaysActionItems';
import DashboardStatsGrid from '../components/dashboard/DashboardStatsGrid';
import AgencySpendSection from '../components/dashboard/AgencySpendSection';
import PaymentSourceStats from '../components/dashboard/PaymentSourceStats';
import DistributorBalanceOverview from '../components/dashboard/DistributorBalanceOverview';
import SalesPerformanceSection from '../components/dashboard/SalesPerformanceSection';
import PerfDashboardWidget from '../components/dashboard/PerfDashboardWidget';
import PendingUploadAlert from '../components/dashboard/PendingUploadAlert';

const DEFAULT_LOW_BALANCE_THRESHOLD = 5000;
const ADMIN_ROLES = new Set(['admin', 'owner', 'assistant', 'accountant']);
const OWNER_ADMIN_ROLES = new Set(['admin', 'owner']);
const VENDOR_ROLES = new Set(['gift_card_vendor', 'recharge_vendor', 'electricity_vendor', 'card_vendor']);

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount || 0);

async function fetchAdminExtras() {
  // PERF: all 6 calls in parallel (was 3 sequential after the initial 3)
  const [balRes, distRes, ownerRes, pending, recoveryRes, monthlyRes] = await Promise.all([
    getDistributorBalance().catch(() => ({ data: [] })),
    getDistributors().catch(() => ({ data: [] })),
    getOwnerDashboardSummary().catch(() => ({ data: null })),
    getPaymentRecovery().catch(() => null),
    api.get('/recovery/stats').catch(() => null),
    api.get('/recovery/monthly-email-runs?limit=6').catch(() => null),
  ]);
  return {
    balRes, distRes, ownerRes,
    pendingSummary: pending?.data?.summary || null,
    recoveryStats: recoveryRes?.data || null,
    monthlyEmailRuns: monthlyRes?.data?.runs || [],
  };
}

function computeLowBalance(balRows, distRows) {
  const agencyBalance = {};
  balRows.forEach((b) => { agencyBalance[b.agency_id] = (b.total_credit || 0) - (b.total_debit || 0); });
  const low = [];
  distRows.forEach((d) => {
    let net = 0;
    (d.agency_ids || []).forEach((aid) => { net += agencyBalance[aid] || 0; });
    const threshold = (d.low_balance_threshold == null || isNaN(Number(d.low_balance_threshold)))
      ? DEFAULT_LOW_BALANCE_THRESHOLD
      : Number(d.low_balance_threshold);
    if (net < threshold) {
      low.push({ name: d.name, balance: net, agencies: d.agency_names || [], threshold });
    }
  });
  return low;
}

export default function DashboardPage() {
  const { user, isDistributor } = useAuth();
  const role = user?.role;
  const isVendor = VENDOR_ROLES.has(role);
  const isAdmin = ADMIN_ROLES.has(role);
  const isOwnerOrAdmin = OWNER_ADMIN_ROLES.has(role);

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [lowBalanceDistributors, setLowBalanceDistributors] = useState([]);
  const [ownerSummary, setOwnerSummary] = useState(null);
  const [pendingSummary, setPendingSummary] = useState(null);
  const [recoveryStats, setRecoveryStats] = useState(null);
  const [monthlyEmailRuns, setMonthlyEmailRuns] = useState([]);

  const refreshMonthlyRuns = useCallback(async () => {
    try {
      const mr = await api.get('/recovery/monthly-email-runs?limit=6');
      setMonthlyEmailRuns(mr.data?.runs || []);
    } catch { /* ignore */ }
  }, []);

  const fetchData = useCallback(async () => {
    if (isDistributor || isVendor) return;
    try {
      setLoading(true);
      const staffId = selectedStaff === 'all' ? undefined : selectedStaff;
      const { data } = await getDashboardStats(staffId);
      setStats(data);

      if (isAdmin) {
        const staffRes = await getStaff();
        setStaffList(staffRes.data.filter((s) => s.role === 'staff'));
        const { balRes, distRes, ownerRes, pendingSummary: ps, recoveryStats: rs, monthlyEmailRuns: mr } = await fetchAdminExtras();
        if (ownerRes.data) setOwnerSummary(ownerRes.data);
        setPendingSummary(ps);
        setRecoveryStats(rs);
        setMonthlyEmailRuns(mr);
        setLowBalanceDistributors(computeLowBalance(balRes.data, distRes.data));
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedStaff, isDistributor, isVendor, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (isDistributor) return <Navigate to="/distributor-dashboard" replace />;
  if (isVendor) return <Navigate to="/gift-card-vendors" replace />;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading...</div></div>;
  }

  const canFilterStaff = role === 'admin' || role === 'owner' || role === 'assistant';

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        {canFilterStaff && (
          <SearchableSelect
            options={[{ value: 'all', label: 'All Staff' }, ...staffList.map((s) => ({ value: s.id, label: s.name, sublabel: s.email }))]}
            value={selectedStaff}
            onValueChange={setSelectedStaff}
            placeholder="Select Staff"
            searchPlaceholder="Search staff..."
            className="w-48"
            data-testid="staff-filter-select"
          />
        )}
      </div>

      {isOwnerOrAdmin && <CriticalRecoveryBanner recoveryStats={recoveryStats} />}
      {ADMIN_ROLES.has(role) && role !== 'accountant' && <PendingUploadAlert />}
      {ADMIN_ROLES.has(role) && role !== 'accountant' && <TodaysActionItems />}
      {/* Hidden from dashboard per user request — admin/owner check dedicated pages
          (/regression-tests, /system-health, /recovery, etc.) for these widgets. */}
      {/* {isOwnerOrAdmin && <TestRunnerLauncher canRun={true} compact={true} />} */}
      {/* {ADMIN_ROLES.has(role) && role !== 'assistant' && <SystemHealthWidget />} */}
      {/* {ADMIN_ROLES.has(role) && role !== 'assistant' && <PerfDashboardWidget role={role} />} */}
      {/* {ADMIN_ROLES.has(role) && role !== 'assistant' && <DailySummaryWidget role={role} />} */}
      {/* <RecoveryEmailHistoryWidget runs={monthlyEmailRuns} role={role} onRefresh={refreshMonthlyRuns} /> */}
      {/* <LowBalanceWarning distributors={lowBalanceDistributors} /> */}
      <PendingRecoveryCard summary={pendingSummary} formatCurrency={formatCurrency} />

      <DashboardStatsGrid stats={stats} formatCurrency={formatCurrency} />
      <AgencySpendSection agencySpend={stats?.agency_spend} formatCurrency={formatCurrency} />
      <PaymentSourceStats sourceStats={stats?.source_stats} formatCurrency={formatCurrency} />
      <DistributorBalanceOverview summary={ownerSummary?.distributor_summary} formatCurrency={formatCurrency} />
      <SalesPerformanceSection performance={ownerSummary?.sales_performance} formatCurrency={formatCurrency} />
    </div>
  );
}
